import BuyShoppingCartIcon from "@/assets/icons/Buy shopping cart.svg?react";
import { AlbumLink } from "@/components/links/AlbumLink";
import type { TrackAlbumDto } from "@/lib/lutely-api/gen/dtos";
import { cn } from "@/lib/utils";
import { Popover, Tooltip } from "@radix-ui/themes";

interface Props {
  albums: TrackAlbumDto[];
  children?: React.ReactNode;
  className?: string;
  /** Accessible name — required when the children are icon-only. */
  label?: string;
  /** Point at the creator's album management pages instead of the storefront. */
  audiolab?: boolean;
}

/**
 * Points at the albums a track belongs to. One album links straight to it;
 * several open a picker, since there is no single right destination. With no
 * album there is nowhere to go, so the children render unlinked.
 */
export const TrackAlbumsLink = ({
  albums,
  children,
  className,
  label,
  audiolab,
}: Props) => {
  const noun = audiolab ? "release" : "album";

  if (albums.length === 0) {
    return <span className={className}>{children}</span>;
  }

  if (albums.length === 1) {
    return (
      <Tooltip content={`View ${noun}: ${albums[0].title}`}>
        <AlbumLink
          album={albums[0]}
          audiolab={audiolab}
          className={className}
          aria-label={label ?? `View ${noun} ${albums[0].title}`}
        >
          {children}
        </AlbumLink>
      </Tooltip>
    );
  }

  return (
    <Popover.Root>
      <Tooltip content={`On ${albums.length} ${noun}s`}>
        <Popover.Trigger>
          <button
            type="button"
            aria-label={label ?? `View the ${albums.length} ${noun}s`}
            className={cn("cursor-pointer text-left", className)}
          >
            {children}
          </button>
        </Popover.Trigger>
      </Tooltip>

      <Popover.Content
        side="bottom"
        align="end"
        sideOffset={8}
        style={{ padding: 0, width: 260, maxWidth: "calc(100vw - 2rem)" }}
      >
        <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg bg-white">
          {albums.map((album) => (
            <li key={album.albumId}>
              <AlbumLink
                album={album}
                audiolab={audiolab}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
                  {album.title}
                </span>
                {!audiolab && (
                  <BuyShoppingCartIcon
                    width={18}
                    height={18}
                    className="shrink-0 text-slate-600"
                    aria-hidden="true"
                  />
                )}
              </AlbumLink>
            </li>
          ))}
        </ul>
      </Popover.Content>
    </Popover.Root>
  );
};
